import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, FileText } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { SUPPORT_EMAIL } from "../lib/billing";

const SECTIONS = [
  {
    title: "1. Sobre MateConMar",
    body: (
      <>
        <p>
          MateConMar (en adelante "la app") es una plataforma web educativa para que niñas y niños
          practiquen matemáticas de primaria y secundaria con ejercicios interactivos, sistema de
          puntos, medallas y rachas.
        </p>
        <p>
          La app se ofrece a través del sitio web actual y funciona en cualquier navegador moderno.
          Para cualquier asunto puedes escribirnos a{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-bold underline text-[#FF9500]">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: "2. Cuentas y modo invitado",
    body: (
      <>
        <p>
          Puedes usar la app como invitado (tu progreso se guarda solo en este dispositivo) o crear
          una cuenta con usuario y contraseña para sincronizarlo en la nube.
        </p>
        <p>
          Si la cuenta pertenece a una persona menor de edad, debe ser creada y administrada con el
          consentimiento de su padre, madre o tutor.
        </p>
      </>
    ),
  },
  {
    title: "3. Prueba gratis de 7 días",
    body: (
      <>
        <p>
          Al registrarte recibes automáticamente <strong>7 días</strong> de acceso Premium completo,
          sin necesidad de tarjeta. La prueba comienza el día del registro y termina exactamente 7 días
          después.
        </p>
        <p>
          Al finalizar la prueba sin suscribirte, tu cuenta pasa al plan gratis (1 ronda de práctica al
          día en Primaria, sin acceso a Secundaria).
        </p>
      </>
    ),
  },
  {
    title: "4. Planes Premium",
    body: (
      <>
        <p>Ofrecemos dos planes Premium, ambos en pesos mexicanos (MXN):</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Mensual</strong> — $99 MXN por 30 días de acceso Premium.
          </li>
          <li>
            <strong>Anual</strong> — $799 MXN por 365 días de acceso Premium.
          </li>
        </ul>
        <p>
          <strong>Importante:</strong> los planes son de <strong>pago único</strong> y{" "}
          <strong>NO se renuevan automáticamente</strong>. No se realizarán cargos recurrentes en tu
          tarjeta. Si deseas seguir teniendo Premium después de la fecha de vencimiento, deberás
          realizar un nuevo pago manualmente desde la sección "Mi Suscripción".
        </p>
      </>
    ),
  },
  {
    title: "5. Procesamiento de pagos",
    body: (
      <>
        <p>
          Todos los pagos se procesan a través de <strong>Stripe</strong>, un proveedor internacional
          certificado PCI-DSS. MateConMar no almacena ni tiene acceso a los datos completos de tu
          tarjeta.
        </p>
        <p>
          El cargo aparecerá en tu estado de cuenta como una transacción de Stripe. Una vez confirmado
          el pago, tu cuenta se actualiza a Premium de forma inmediata.
        </p>
      </>
    ),
  },
  {
    title: "6. Política de reembolsos",
    body: (
      <>
        <p>
          Si no estás satisfecho con tu compra, puedes solicitar un <strong>reembolso completo dentro
          de los 7 días naturales</strong> posteriores al pago, sin necesidad de explicar el motivo.
        </p>
        <p>
          Para solicitar un reembolso, envía un correo a{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-bold underline text-[#FF9500]">
            {SUPPORT_EMAIL}
          </a>{" "}
          con tu nombre de usuario y la fecha aproximada del pago. Procesaremos la solicitud en un
          plazo máximo de 5 días hábiles. El reembolso se realizará a la misma tarjeta utilizada en
          el pago.
        </p>
        <p>
          Pasados los 7 días, no se otorgarán reembolsos, pero tu acceso Premium continuará hasta su
          fecha de vencimiento natural.
        </p>
      </>
    ),
  },
  {
    title: "7. Cómo cancelar",
    body: (
      <>
        <p>
          Al tratarse de pagos únicos, <strong>no necesitas cancelar nada</strong> para evitar futuros
          cargos: simplemente no realices un nuevo pago al vencer tu plan.
        </p>
        <p>
          Si quieres eliminar tu cuenta completamente, escríbenos a{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-bold underline text-[#FF9500]">
            {SUPPORT_EMAIL}
          </a>{" "}
          y la cerraremos definitivamente.
        </p>
      </>
    ),
  },
  {
    title: "8. Uso aceptable",
    body: (
      <>
        <p>Al usar MateConMar te comprometes a:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>No compartir tu cuenta con personas ajenas a tu núcleo familiar.</li>
          <li>No intentar romper, copiar o revender el contenido de la app.</li>
          <li>No usar la app para fines distintos al aprendizaje personal.</li>
        </ul>
      </>
    ),
  },
  {
    title: "9. Datos personales",
    body: (
      <>
        <p>
          Para crear una cuenta solicitamos: nombre, apellido, correo electrónico, usuario y contraseña.
          El correo se utiliza para identificación de tu cuenta y para enviarte información importante
          relacionada con tu suscripción (por ejemplo, recordatorios o avisos de cambios).
        </p>
        <p>
          Si la cuenta pertenece a una persona menor de edad, los datos deben ser proporcionados por su
          padre, madre o tutor.
        </p>
        <p>
          Tus datos personales y de progreso (puntos, medallas, rachas) se almacenan de forma segura
          y solo se utilizan para ofrecer el servicio. No los vendemos ni los compartimos con terceros
          con fines comerciales.
        </p>
        <p>
          Si quieres acceder, modificar o eliminar tus datos, escríbenos a{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-bold underline text-[#FF9500]">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: "10. Cambios en los términos",
    body: (
      <>
        <p>
          Podemos actualizar estos términos para mejorar el servicio o cumplir requisitos legales.
          Cualquier cambio importante será comunicado dentro de la app antes de aplicarse. Si los
          cambios afectan a tu plan ya pagado, mantendremos las condiciones originales hasta el
          vencimiento de ese plan.
        </p>
      </>
    ),
  },
  {
    title: "11. Contacto",
    body: (
      <>
        <p>
          Para cualquier duda, queja, sugerencia o solicitud de reembolso, escríbenos a:{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-bold underline text-[#FF9500]">
            {SUPPORT_EMAIL}
          </a>
        </p>
        <p className="text-xs text-[#5C5C60] mt-3">
          Última actualización: febrero de 2026.
        </p>
      </>
    ),
  },
];

export default function Terminos() {
  const navigate = useNavigate();
  return (
    <PageShell>
      <div className="mt-4">
        <ClayButton variant="ghost" onClick={() => navigate(-1)} data-testid="terms-back-btn">
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <div className="max-w-3xl mx-auto mt-4">
        <div className="inline-flex items-center gap-3 rounded-full px-4 py-2 mb-4 bg-[#FFF6CC]">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#FF9500]">
            <FileText className="w-5 h-5 text-white" strokeWidth={3} />
          </span>
          <span className="font-bold text-base text-[#7A5C00]">Documentos legales</span>
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2" data-testid="terms-title">
          Términos y Política de Reembolso
        </h1>
        <p className="text-base text-[#5C5C60] mb-6">
          Por favor lee estos términos antes de suscribirte. Están escritos en lenguaje claro y resumen tus derechos.
        </p>

        <div className="bg-white rounded-3xl border-4 border-white shadow-md p-5 sm:p-8 space-y-7" data-testid="terms-content">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#1C1C1E] mb-2">
                {s.title}
              </h2>
              <div className="text-base text-[#3A3A3D] space-y-2.5 leading-relaxed">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-6 bg-[#FFF6CC] border-2 border-[#FFD700] rounded-2xl p-4 flex items-start gap-3">
          <Mail className="w-5 h-5 text-[#7A5C00] shrink-0 mt-0.5" strokeWidth={3} />
          <div className="text-sm text-[#7A5C00]">
            <p className="font-bold">¿Dudas?</p>
            <p>
              Escríbenos a{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-bold">
                {SUPPORT_EMAIL}
              </a>{" "}
              y te respondemos lo más pronto posible.
            </p>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
